/**
 * ServersDashboard — portada de Servidores (/infrastructure/servers).
 *
 * El listado completo sigue en /infrastructure/servers/list. Aquí se responde a
 * cuatro preguntas de un vistazo: cuántos hay, cuántos reportan por agente, qué
 * licencias están en problemas y cómo están repartidos por estado y rack.
 *
 * Y, sobre todo, el bloque de **vulnerabilidades** (`ciType="rack_element"`):
 * qué software de los servidores tiene fallos conocidos.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ServerIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  SignalIcon,
  KeyIcon,
  RectangleStackIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import VulnerabilitiesPanel from './VulnerabilitiesPanel';
import { COLORS, pct, countBy, Tile, Donut, Legend, BarRow, Card, EmptyState } from './DashboardBits';

const STATUS_LABEL = {
  active: 'srv.status.active',
  maintenance: 'srv.status.maintenance',
  offline: 'srv.status.offline',
};

const STATUS_COLOR = {
  active: COLORS.ok,
  maintenance: COLORS.warn,
  offline: COLORS.bad,
};

const AGENT_LABEL = {
  online: 'srv.agent.online',
  offline: 'srv.agent.offline',
  none: 'srv.agent.none',
  unknown: 'srv.agent.unknown',
};

const AGENT_COLOR = {
  online: COLORS.ok,
  offline: COLORS.warn,
  none: COLORS.gray,
  unknown: COLORS.muted,
};

const LICENSE_LABEL = {
  valid: 'srv.license.valid',
  vigente: 'srv.license.valid',
  expired: 'srv.license.expired',
  caducada: 'srv.license.expired',
  expiring: 'srv.license.expiring',
  none: 'srv.license.none',
  unknown: 'srv.license.unknown',
};

/** Clave de traducción conocida o, si el backend manda un valor nuevo, el valor legible. */
const labelOf = (map, key, t) =>
  map[key] ? t(map[key]) : `${key}`.charAt(0).toUpperCase() + `${key}`.slice(1);

export default function ServersDashboard({ companyId }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp = await api.getServers(companyId);
      setServers(Array.isArray(resp?.data) ? resp.data : []);
    } catch (err) {
      setError(err.message || t('srv.errorTitle'));
    } finally {
      setLoading(false);
    }
  }, [companyId, t]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const total = servers.length;
    const byStatus = countBy(servers, (s) => s.status || 'active');
    const byAgent = countBy(servers, (s) => s.agent_status || 'none');
    const byLicense = countBy(servers, (s) => s.license_status || 'none');
    const byRack = countBy(servers, (s) => s.rack_name || 'Sin rack');
    const online = servers.filter((s) => s.agent_status === 'online').length;
    const expired = servers.filter((s) => s.license_status === 'expired').length;
    const ram = servers.reduce((sum, s) => sum + (Number(s.ram_gb) || 0), 0);
    const storage = servers.reduce((sum, s) => sum + (Number(s.storage_tb) || 0), 0);
    return { total, byStatus, byAgent, byLicense, byRack, online, expired, ram, storage };
  }, [servers]);

  // Cualquier enlace con parámetros (destacar, abrir, q…) es del listado de
  // siempre: se reenvía tal cual para no romper los enlaces internos.
  const deepQuery = searchParams.toString();
  if (deepQuery) return <Navigate to={`/infrastructure/servers/list?${deepQuery}`} replace />;

  if (!companyId) {
    return (
      <div className="dbx-page">
        <EmptyState icon={ServerIcon} title={t('srv.selectCompany')} text={t('srv.selectCompanyText')} />
      </div>
    );
  }

  const statusSegments = stats.byStatus.map((item) => ({
    key: item.key,
    label: labelOf(STATUS_LABEL, item.key, t),
    value: item.value,
    color: STATUS_COLOR[item.key] || COLORS.info,
  }));  const agentSegments = stats.byAgent.map((item) => ({
    key: item.key,
    label: labelOf(AGENT_LABEL, item.key, t),
    value: item.value,
    color: AGENT_COLOR[item.key] || COLORS.gray,
  }));
  const agentCoverage = pct(stats.online, stats.total);

  return (
    <div className="dbx-page">
      <header className="dbx-head">
        <div>
          <h1>{t('srv.title')}</h1>
          <p>{t('srv.subtitle')}</p>
        </div>
        <div className="dbx-head-actions">
          <button type="button" className="dbx-btn" onClick={load} disabled={loading}>
            <ArrowPathIcon />
            {t('srv.refresh')}
          </button>
          <Link to="/infrastructure/servers/list" className="dbx-btn dbx-btn--primary">
            {t('srv.openList')}
            <ArrowRightIcon />
          </Link>
        </div>
      </header>

      {error && (
        <div className="dbx-state">
          <ExclamationTriangleIcon />
          <h2>{t('srv.errorTitle')}</h2>
          <p>{error}</p>
          <button type="button" className="dbx-btn" onClick={load}>{t('srv.retry')}</button>
        </div>
      )}

      {!error && loading && (
        <div className="dbx-state"><ArrowPathIcon /><p>{t('srv.loading')}</p></div>
      )}

      {!error && !loading && stats.total === 0 && (
        <EmptyState
          icon={ServerIcon}
          title={t('srv.empty.title')}
          text={t('srv.empty.text')}
          action={<Link to="/infrastructure/servers/list" className="dbx-btn dbx-btn--primary">{t('srv.goList')}</Link>}
        />
      )}

      {!error && !loading && stats.total > 0 && (
        <>
          <div className="dbx-tiles">
            <Tile
              icon={ServerIcon}
              tone="info"
              label={t('srv.kpi.servers')}
              value={stats.total}
              hint={stats.byRack.length === 1
                ? t('srv.kpi.locationsOne')
                : t('srv.kpi.locations', { count: stats.byRack.length })}
            />
            <Tile
              icon={SignalIcon}
              tone={agentCoverage >= 80 ? 'ok' : 'warn'}
              label={t('srv.kpi.agent')}
              value={`${agentCoverage}%`}
              bar={agentCoverage}
              hint={t('srv.kpi.onOf', { online: stats.online, total: stats.total })}
            />
            <Tile
              icon={KeyIcon}
              tone={stats.expired ? 'bad' : 'ok'}
              label={t('srv.kpi.licenses')}
              value={stats.expired}
              hint={stats.expired ? t('srv.kpi.expired') : t('srv.kpi.noneExpired')}
            />
            <Tile
              icon={RectangleStackIcon}
              tone="info"
              label={t('srv.kpi.capacity')}
              value={`${Math.round(stats.storage)} TB`}
              hint={t('srv.kpi.ram', { count: stats.ram.toLocaleString('es-ES') })}
            />
          </div>

          <div className="dbx-grid">
            <Card icon={ServerIcon} title={t('srv.card.status')} subtitle={t('srv.card.statusSub')}>
              <div className="dbx-split">
                <Donut segments={statusSegments} center={stats.total} label={t('srv.kpi.servers').toLowerCase()} />
                <Legend items={statusSegments} total={stats.total} />
              </div>
            </Card>

            <Card icon={ShieldCheckIcon} tone="ok" title={t('srv.card.agent')} subtitle={t('srv.card.agentSub')}>
              <div className="dbx-card-body">
                {agentSegments.map((item) => (
                  <BarRow
                    key={item.key}
                    label={item.label}
                    value={item.value}
                    total={stats.total}
                    color={item.color}
                  />
                ))}
              </div>
            </Card>

            <Card icon={KeyIcon} title={t('srv.card.licenses')} subtitle={t('srv.card.licensesSub')}>
              <div className="dbx-card-body">
                {stats.byLicense.map((item) => (
                  <BarRow
                    key={item.key}
                    label={labelOf(LICENSE_LABEL, item.key, t)}
                    value={item.value}
                    total={stats.total}
                    color={item.key === 'expired' || item.key === 'caducada' ? COLORS.bad : COLORS.info}
                  />
                ))}
              </div>
            </Card>

            <Card icon={RectangleStackIcon} title={t('srv.card.racks')} subtitle={t('srv.card.racksSub')}>
              <div className="dbx-card-body">
                {stats.byRack.slice(0, 6).map((item) => (
                  <BarRow
                    key={item.key}
                    label={item.key}
                    value={item.value}
                    total={stats.total}
                    color={COLORS.info}
                  />
                ))}
              </div>
            </Card>
          </div>

          <VulnerabilitiesPanel companyId={companyId} ciType="rack_element" />
        </>
      )}
    </div>
  );
}
